import React from 'react';
import { useSelector } from 'react-redux';

import { L10nDate, L10nNumber } from '@travel/i18n';
import { Checkmark, RakutenPoints, TranslateBadge } from '@travel/icons/ui';
import { MOBILE_SCREEN, useDeviceType } from '@travel/traveler-core/hooks';
import usePriceLabel from '@travel/traveler-core/hooks/usePriceLabel';
import { IconText, Tag } from '@travel/ui';
import { cx } from '@travel/utils';

import MembersOnlyTag from 'components/MembersOnlyTag';
import Money from 'components/Money';
import PlanPaymentRestriction from 'components/PlanPaymentRestriction';
import PriceLabel from 'components/PriceLabel';
import TimeZoneInfo from 'components/TimeZoneInfo';

import { getIsDirectContractType } from 'store/providerInformation/selectors';
import {
  getNoOfAppliedCoupons,
  getNoOfAvailableCoupons,
  getPlanDetail,
} from 'store/providerRatePlan/selectors';

import { OFFER_TYPE } from 'constants/salesPromotion';
import { Translate } from 'core/translate';
import type { Plan } from 'ProviderRatePlan-Types';

import PlanCouponItem from '../PlanCouponItem';
import ReserveButton from '../ReserveButton';
import type { Props as PlanDetailHeaderProps } from './PlanItem';

import styles from './roomList.module.scss';

type Props = {
  onDisplayPlanDetail?: () => void;
  onReserve: () => void;
} & PlanDetailHeaderProps;

function PlanDetailHeaderMain(props: Props) {
  const { searchParam, onSelectDate } = props;

  const deviceType = useDeviceType();
  const isMobile = deviceType === MOBILE_SCREEN;
  const isDirectContract = useSelector(getIsDirectContractType);

  const plan: Plan = useSelector(
    getPlanDetail(props.itemId, props.planId, isDirectContract, props.ratePlanId),
  );

  const noOfAppliedCoupons: number = useSelector(
    getNoOfAppliedCoupons(props.itemId, props.planId, isDirectContract, props.ratePlanId),
  );
  const noOfAvailableCoupons: number = useSelector(
    getNoOfAvailableCoupons(props.itemId, props.planId, isDirectContract, props.ratePlanId),
  );

  const isCoupon = plan?.price?.salesPromotions?.some((sp) => sp.offerType === OFFER_TYPE.COUPON);

  const reserveButton = (
    <ReserveButton
      wrapperClassName={styles.reserveButtonWrapper}
      onSelectDate={onSelectDate}
      searchParam={searchParam}
      onClick={props.onReserve}
      isRakutenMembership={plan?.hasMemberOnly}
    />
  );

  // coupon element
  const couponBadge = (
    <PlanCouponItem
      itemId={props.itemId}
      ratePlanId={props.ratePlanId}
      planId={props.planId}
      combinedIncentive={plan?.price.combinedIncentive}
      salesPromotions={plan?.price.salesPromotions}
      priceAfterDiscount={plan?.price.total}
      noOfAppliedCoupons={noOfAppliedCoupons}
      noOfAvailableCoupons={noOfAvailableCoupons}
      query={props.query}
      pageIdentifier={props.providerId}
    />
  );

  const freeUntil = plan?.cancellationPenalties?.find((item) => item.type === 'NO_PENALTY');

  const planItemHeader = <h4 className={cx(styles.planItemName)}>{plan?.name}</h4>;

  const { guestLabel, nightLabel, chargeUnitLabel, roomsLabelText } = usePriceLabel(searchParam);

  return (
    <>
      <div
        className={cx(styles.planItemInfoWrapper)}
        data-testid="ratePlanList-planDetailHeader-wrapper"
      >
        <div className={styles.planItemInfoBox}>
          {planItemHeader}
          {plan?.hasMemberOnly && (
            <MembersOnlyTag text={<Translate id="Provider_Info.Room_List.Members_Only_Tag" />} />
          )}
          {freeUntil?.deadline && (
            <IconText
              className={styles.planItemFreeUntil}
              text={
                <div>
                  <p>
                    <Translate
                      id="Cancellation_Policy_Common.Provider_Pages.Free_Cancellation"
                      data={{
                        free_cancellation_date: (
                          <>
                            <L10nDate value={freeUntil.deadline} format="L" isDisplayAsLocalTime />
                            &nbsp;
                            <L10nDate value={freeUntil.deadline} format="LT" isDisplayAsLocalTime />
                            <TimeZoneInfo
                              timeZoneData={freeUntil?.timeZoneData}
                              isIndirectContract={!isDirectContract}
                            />
                          </>
                        ),
                      }}
                    />
                  </p>
                </div>
              }
              icon={<Checkmark size={16} />}
              spaceBetween={4}
            />
          )}

          {plan?.paymentRestrictionFeature && (
            <Tag type="filled" color="gray" className={styles.paymentRestrictionTag}>
              <PlanPaymentRestriction
                titleClassName={styles.paymentRestriction}
                {...plan?.paymentRestrictionFeature}
              />
            </Tag>
          )}

          {Number(plan?.points?.amount) > 0 && (
            <span className={styles.planItemPoint}>
              <RakutenPoints size={16} />
              <Translate
                id="Provider_Info.Room_List.Plan_Info.Points"
                data={{
                  points_calculation: <L10nNumber value={plan?.points?.amount || 0} />,
                  currency: `${plan?.points?.currency || ''}`,
                }}
              />
            </span>
          )}

          {!isMobile && isCoupon && couponBadge}

          {!isMobile && (
            <div
              className={cx(styles.planSubDetailsBox, styles.card)}
              data-testid="planDetailHeader-planSubDetailsBox-wrapper"
            >
              {plan?.description && (
                <>
                  <p className={cx(styles.planItemDescription, styles.card)}>{plan?.description}</p>
                  {plan?.isDescriptionGoogleTranslated && (
                    <p className={cx(styles.card, styles.planDescriptionTranslateBadge)}>
                      <TranslateBadge data-testid="planDetailHeader-providerDescription-translateBadge" />
                    </p>
                  )}
                </>
              )}
            </div>
          )}
        </div>
        <div className={styles.planItemInfoBox}>
          <PriceLabel
            className={styles.priceLabel}
            isPromotionLabelHidden={false}
            price={plan?.price}
            planPaymentRestrictionFeature={plan?.paymentRestrictionFeature}
            PriceComponent={(props: { value: number }) => (
              <Money {...props} roundPosition={isDirectContract ? null : -1} />
            )}
            chargeUnit={
              isDirectContract ? (
                chargeUnitLabel
              ) : (
                <Translate id="Provider_List.Out_To_Out.Search_Results.Per_Room_Per_Night" />
              )
            }
            roomsLabel={roomsLabelText}
            guestsLabel={guestLabel}
            nightsLabel={nightLabel}
            taxesLabel={
              <Translate
                id={
                  plan?.price?.isConsumptionTaxIncluded
                    ? 'Provider_List.Search_Results.Taxes'
                    : 'Provider_List.Luxury.Tax_Excluded'
                }
                className={styles.providerInfoLabel}
              />
            }
            isDirectContract={isDirectContract}
          />
          {isMobile && isCoupon && couponBadge}
          {!isMobile && <div className={styles.reserveButtonWrapper}>{reserveButton}</div>}
        </div>
      </div>

      {isMobile && plan?.description && (
        <>
          <p className={styles.planItemDescription}>{plan?.description}</p>
          {plan?.isDescriptionGoogleTranslated && (
            <p className={styles.planDescriptionTranslateBadge}>
              <TranslateBadge data-testid="planDetailHeader-providerDescription-translateBadge" />
            </p>
          )}
        </>
      )}
    </>
  );
}

export default React.memo(PlanDetailHeaderMain);
